import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Button, Text, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import {
  TrainingClassDetailIndexPageFragment,
  TrainingsTrainingClassModalityEnum,
  UpdateTrainingClassMutationVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateTrainingClass } from 'modules/trainings/hooks/TrainingClasses/useUpdateTrainingClass'
import type { TrainingsTrainingClass } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

interface ClassBasicInfoFormValues {
  name: string
  description?: string
  mode: TrainingsTrainingClassModalityEnum
  startDate: string
  endDate: string
  codigoGrupo?: string
}

const EditClassModal = () => {
  const { pathname } = useLocation()

  const { id, classId } = useParams()
  const { update } = useUpdateTrainingClass()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { currentClass, classes } = useOutletContext<{
    currentClass: TrainingClassDetailIndexPageFragment
    canEdit: boolean
    classes: TrainingsTrainingClass[]
  }>()

  const redirectToGroup = pathname.includes('sessions')
  const navigateToClass = () => {
    if (redirectToGroup) {
      navigate(
        navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
          id: Number(id),
          classId: String(classId),
        })
      )
    } else {
      navigate(
        navigation.trainings.list.details_revamp.view.trainingClasses.tab.fullPath({
          id: Number(id),
        })
      )
    }
  }

  const current = useUnsafeMemo(() => {
    return currentClass ?? classes.find((x) => x.id === classId)
  }, [currentClass, classes, classId])

  if (!current) throw new Error('There should be a class defined here')

  const form = useForm<ClassBasicInfoFormValues>({
    defaultValues: {
      mode: current?.mode,
      name: String(current?.name),
      description: current?.description || '',
      startDate: !!current?.startDate ? current.startDate : '',
      endDate: !!current?.endDate ? current.endDate : '',
      codigoGrupo: current.fundaeConfig?.codigoGrupo,
    },
    validate: (data) => {
      const errors: FormErrors<ClassBasicInfoFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }
      if (!data.startDate) {
        //Required because there are training classes with null start date in production
        errors.startDate = i18n.t('trainings.trainings_revamp.classes_sidepanel.error_start_empty')
      }
      if (!data.endDate) {
        //Required because there are training classes with null end date in production
        errors.endDate = i18n.t('trainings.trainings_revamp.classes_sidepanel.error_end_empty')
      }
      if (
        data.startDate &&
        data.endDate &&
        stringToDate(data.endDate, 'ui') < stringToDate(data.startDate, 'ui')
      ) {
        //Required because there are training classes with end date before start date in production
        errors.endDate = i18n.t(
          'trainings.trainings_revamp.classes_sidepanel.error_end_before_start'
        )
      }
      return errors
    },
    validateOnMount: true,
    onSubmit: async (data) => {
      const { codigoGrupo, ...rest } = data
      const payload: UpdateTrainingClassMutationVariables = {
        ...rest,
        endDate: data.endDate,
        startDate: data.startDate,
        id: Number(current.id),
      }
      if (codigoGrupo) {
        payload.fundaeConfiguration = {
          codigoGrupo,
        }
      }

      const result = await update(payload)
      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.trainings_revamp.classes_sidepanel.error_message'),
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.trainings_revamp.classes_sidepanel.success_message'),
        })
        navigateToClass()
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('trainings.trainings_revamp.classes.edit_class_modal.edit_class')}
      illustration="document-folder-board-edit"
      onClose={navigateToClass}
      onSubmit={() => {
        track(trackingEvents.training.trainingClass.saveEditClass)
        form.handleSubmit()
      }}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button.Router
            type="secondary"
            to={
              redirectToGroup
                ? navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath(
                    {
                      id: Number(id),
                      classId: String(classId),
                    }
                  )
                : navigation.trainings.list.details_revamp.view.trainingClasses.tab.fullPath({
                    id: Number(id),
                  })
            }
          >
            {i18n.t('common.cancel')}
          </Button.Router>
          <Button submit>{i18n.t('common.save')}</Button>
        </Box>
      }
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('trainings.trainings_revamp.classes_sidepanel.detail.name')}
          value={form.values.name}
          onChange={(value: string) => form.setValues({ ...form.values, name: value })}
          mandatory
          error={form.errors.name}
        />
        <Box flexDirection="row" gap="s16">
          <DayPicker
            value={form.values.startDate}
            mode="single"
            label={i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.start_date'
            )}
            onChange={(value) => form.setValues({ ...form.values, startDate: value })}
            mandatory
            maxDate={form.values.endDate ? moment(form.values.endDate) : undefined}
            error={form.errors.startDate}
            valueFormat="backend"
            format="ui"
          />
          <DayPicker
            value={form.values.endDate}
            mode="single"
            label={i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.end_date'
            )}
            onChange={(value) => form.setValues({ ...form.values, endDate: value })}
            mandatory
            minDate={form.values.startDate ? moment(form.values.startDate) : undefined}
            error={form.errors.endDate}
            valueFormat="backend"
            format="ui"
          />
        </Box>
        <Box gap="s8">
          <Text.Normal size="200" color="grey1000" weight="medium">
            {i18n.t('trainings.trainings_revamp.classes_sidepanel.detail.description')}
          </Text.Normal>
          <TextEditor
            placeholder=""
            onChange={(data) => {
              form.setValues({ ...form.values, description: String(data) })
            }}
            initialEditorState={{
              data: form.values.description,
            }}
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default EditClassModal
